import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Chip,
  IconButton,
  Tooltip,
  Alert,
  Snackbar,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import BadgeIcon from '@mui/icons-material/Badge';
import PeopleIcon from '@mui/icons-material/People';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import CloseIcon from '@mui/icons-material/Close';

import { EmployeeService } from '../../services/payrollService';
import { Employee, JobTitle } from '../../types/payroll';
import { formatCurrency } from '../../utils/formatters';
import { useRole } from '../../hooks/useRole';
import ConfirmDialog from '../common/ConfirmDialog';
import EmployeeDialog from './EmployeeDialog';
import EmployeeImportDialog from './EmployeeImportDialog';

// Import reusable components
import PageHeader from '../common/PageHeader';
import DataGrid, { Column } from '../common/DataGrid';
import FilterBar, { FilterConfig } from '../common/FilterBar';
import StatsSummary from '../common/StatsSummary';

// Import styles for consistent layout
import '../../styles/Payroll.css';
import '../../styles/EmployeeList.css';

const EmployeeList: React.FC = () => {
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [jobTitles, setJobTitles] = useState<JobTitle[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [initialized, setInitialized] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState<number>(0);
  const [rowsPerPage, setRowsPerPage] = useState<number>(10);
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [jobTitleFilter, setJobTitleFilter] = useState<string>('all');
  const [confirmDialogOpen, setConfirmDialogOpen] = useState<boolean>(false);
  const [employeeToDelete, setEmployeeToDelete] = useState<Employee | null>(null);
  
  // Dialog states
  const [employeeDialogOpen, setEmployeeDialogOpen] = useState<boolean>(false);
  const [selectedEmployee, setSelectedEmployee] = useState<Employee | null>(null);
  const [importDialogOpen, setImportDialogOpen] = useState<boolean>(false);
  const [actionMenuAnchor, setActionMenuAnchor] = useState<null | HTMLElement>(null);
  
  // Notification state
  const [notification, setNotification] = useState<{
    message: string;
    type: 'success' | 'error' | 'warning';
    show: boolean;
  }>({
    message: '',
    type: 'success',
    show: false
  });
  
  // Ref to prevent duplicate initialization
  const initializationRef = useRef<boolean>(false);
  
  const navigate = useNavigate();
  const { hasAnyRole } = useRole();
  // Temporarily allow all users to manage employees for testing
  const canManageEmployees = true; // hasAnyRole(['hr', 'admin']);

  // Notification helper function
  const showNotification = (message: string, type: 'success' | 'error' | 'warning' = 'success') => {
    setNotification({ message, type, show: true });
    setTimeout(() => {
      setNotification(prev => ({ ...prev, show: false }));
    }, 4000);
  };

  // Close notification manually
  const closeNotification = () => {
    setNotification(prev => ({ ...prev, show: false }));
  };

  // Initialize data function
  const initializeData = async () => {
    if (initializationRef.current) return;
    initializationRef.current = true;
    
    try {
      setLoading(true);
      setError(null);
      
      const [employeesResponse, jobTitlesResponse] = await Promise.all([
        EmployeeService.getAllEmployees({ withSalary: true }),
        EmployeeService.getJobTitles()
      ]);
      
      setEmployees(employeesResponse.data || []);
      setJobTitles(jobTitlesResponse.data || []);
      setInitialized(true);
      
    } catch (err: any) {
      const errorMessage = err.response?.data?.message || err.message || 'Failed to load employees';
      setError(errorMessage);
      showNotification(errorMessage, 'error');
      console.error('Error fetching employee data:', err);
    } finally {
      setLoading(false);
    }
  };

  // Refresh data function
  const refreshData = async () => {
    initializationRef.current = false;
    await initializeData();
  };

  useEffect(() => {
    initializeData();
  }, []);

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleAddEmployee = () => {
    console.log('=== EmployeeList: handleAddEmployee called ===');
    console.log('Opening employee dialog with no selected employee...');
    setSelectedEmployee(null);
    setEmployeeDialogOpen(true);
    console.log('Employee dialog should now be open with no employee selected');
  };

  const handleEditEmployee = async (id: number) => {
    try {
      console.log('=== EmployeeList: handleEditEmployee called ===');
      console.log('Employee ID to edit:', id);
      
      // Show loading state
      setLoading(true);
      
      // Fetch the full employee details including all name fields
      console.log('Fetching employee data from API...');
      const response = await EmployeeService.getEmployeeById(id);
      console.log('API response:', response);
      
      if (response && response.data) {
        // Log the employee data to verify all fields are present
        console.log('Employee data received from API:', response.data);
        console.log('Employee name fields:', {
          first_name_en: response.data.first_name_en,
          last_name_en: response.data.last_name_en,
          first_name_vi: response.data.first_name_vi,
          last_name_vi: response.data.last_name_vi,
          full_name: response.data.full_name
        });
        
        // Set the complete employee data with all fields
        console.log('Setting selectedEmployee state...');
        setSelectedEmployee(response.data);
        console.log('Opening employee dialog...');
        setEmployeeDialogOpen(true);
        console.log('Employee dialog should now be open with employee:', response.data);
      } else {
        console.error('No employee data in response:', response);
        showNotification('Failed to load employee details', 'error');
      }
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || error.message || 'Failed to load employee details';
      console.error('Error in handleEditEmployee:', error);
      showNotification(errorMessage, 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleViewEmployee = (id: number) => {
    navigate(`/payroll/employees/${id}`);
  };

  const handleImportEmployees = () => {
    setImportDialogOpen(true);
  };

  const handleActionMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setActionMenuAnchor(event.currentTarget);
  };

  const handleActionMenuClose = () => {
    setActionMenuAnchor(null);
  };

  const handleDeleteClick = (employee: Employee) => {
    setEmployeeToDelete(employee);
    setConfirmDialogOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!employeeToDelete) return;
    
    try {
      setLoading(true);
      await EmployeeService.deleteEmployee(employeeToDelete.id);
      
      // Remove from list
      setEmployees(employees.filter(emp => emp.id !== employeeToDelete.id));
      setConfirmDialogOpen(false);
      setEmployeeToDelete(null);
      
      showNotification(`Employee ${employeeToDelete.full_name || employeeToDelete.employee_no} deleted successfully`, 'success');
    } catch (err: any) {
      const errorMessage = err.response?.data?.message || 'Failed to delete employee';
      setError(errorMessage);
      showNotification(errorMessage, 'error');
      console.error('Error deleting employee:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleCancelDelete = () => {
    setConfirmDialogOpen(false);
    setEmployeeToDelete(null);
  };

  const handleEmployeeSave = (employee: Employee) => {
    // Log the saved employee data to verify all fields are present
    console.log('Employee data being saved:', employee);
    
    // Update the employees list
    if (selectedEmployee) {
      // Update existing employee
      setEmployees(prev => prev.map(emp => emp.id === employee.id ? employee : emp));
      
      // Use first_name_en and last_name_en for display if full_name is not available
      const displayName = employee.full_name || 
        `${employee.first_name_en} ${employee.last_name_en || ''}`.trim() || 
        employee.employee_no;
        
      showNotification(`Employee ${displayName} updated successfully`, 'success');
    } else {
      // Add new employee
      setEmployees(prev => [...prev, employee]);
      
      // Use first_name_en and last_name_en for display if full_name is not available
      const displayName = employee.full_name || 
        `${employee.first_name_en} ${employee.last_name_en || ''}`.trim() || 
        employee.employee_no;
        
      showNotification(`Employee ${displayName} created successfully`, 'success');
    }
    
    // Refresh data to ensure we have the latest employee information
    refreshData();
    
    setEmployeeDialogOpen(false);
    setSelectedEmployee(null);
  };

  const handleImportComplete = (result: any) => {
    if (result.success) {
      showNotification(`Successfully imported ${result.imported || 0} employees`, 'success');
      // Refresh the employee list
      refreshData();
    } else {
      showNotification(result.message || 'Import failed', 'error');
    }
    setImportDialogOpen(false);
  };

  const filteredEmployees = employees.filter(employee => {
    // Search term filter
    const matchesSearch = searchTerm === '' || 
      employee.full_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      employee.employee_no?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      employee.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      employee.national_id?.toLowerCase().includes(searchTerm.toLowerCase());
    
    // Status filter
    const matchesStatus = statusFilter === 'all' || employee.status === statusFilter;
    
    // Job title filter
    const matchesJobTitle = jobTitleFilter === 'all' || 
      (employee.job_title_id != null && employee.job_title_id.toString() === jobTitleFilter);
    
    return matchesSearch && matchesStatus && matchesJobTitle;
  });

  // Apply pagination
  const paginatedEmployees = filteredEmployees.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  // Define columns for the data grid
  const columns: Column<Employee>[] = [
    {
      id: 'employee_no',
      label: 'Employee ID',
      minWidth: 100,
      format: (value) => value
    },
    {
      id: 'full_name',
      label: 'Name',
      minWidth: 180,
      format: (value, row) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <BadgeIcon fontSize="small" sx={{ color: 'action.active', opacity: 0.6 }} />
          <span>{row.full_name || `${row.first_name_en} ${row.last_name_en || ''}`}</span>
        </Box>
      )
    },
    {
      id: 'job_title_id',
      label: 'Job Title',
      minWidth: 150,
      format: (value, row) => jobTitles.find(jt => jt.id === row.job_title_id)?.title_name_en || '—'
    },
    {
      id: 'status',
      label: 'Status',
      minWidth: 100,
      format: (value) => (
        <Chip
          label={value}
          color={value === 'active' ? 'success' : 'default'}
          size="small"
          sx={{ minWidth: '70px' }}
        />
      )
    },
    {
      id: 'email',
      label: 'Email',
      minWidth: 180,
      format: (value) => value || '—'
    },
    {
      id: 'national_id',
      label: 'National ID',
      minWidth: 120,
      format: (value) => value || '—'
    },
    {
      id: 'salary_profile',
      label: 'Base Salary',
      minWidth: 120,
      format: (value) => value ? formatCurrency(value.base_salary_vnd, 'VND') : '—'
    },
    {
      id: 'actions',
      label: 'Actions',
      minWidth: 120,
      align: 'center',
      format: (_, row) => (
        <Box sx={{ display: 'flex', justifyContent: 'center', gap: 0.5 }}>
          <Tooltip title="View Details">
            <IconButton
              size="small"
              onClick={() => handleViewEmployee(row.id)}
              sx={{ color: 'info.main' }}
            >
              <BadgeIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          
          {canManageEmployees && (
            <>
              <Tooltip title="Edit Employee">
                <IconButton
                  size="small"
                  onClick={() => handleEditEmployee(row.id)}
                  sx={{ color: 'primary.main' }}
                >
                  <EditIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              
              <Tooltip title="Delete Employee">
                <IconButton
                  size="small"
                  onClick={() => handleDeleteClick(row)}
                  sx={{ color: 'error.main' }}
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </>
          )}
        </Box>
      )
    }
  ];

  // Define filter configurations
  const filterConfigs: FilterConfig[] = [
    {
      id: 'status',
      label: 'Status',
      value: statusFilter,
      options: [
        { value: 'all', label: 'All Status' },
        { value: 'active', label: 'Active' },
        { value: 'inactive', label: 'Inactive' }
      ]
    },
    {
      id: 'jobTitle',
      label: 'Job Title',
      value: jobTitleFilter,
      options: [
        { value: 'all', label: 'All Titles' },
        ...jobTitles.map(title => ({
          value: title.id.toString(),
          label: title.title_name_en
        }))
      ]
    }
  ];

  return (
    <Box className="payroll-container employee-list-container" sx={{ p: 2 }}>
      {/* Header Section - Using the reusable PageHeader component */}
      <PageHeader
        title="Employees"
        subtitle="Manage employee information"
        icon={<BadgeIcon />}
        onRefresh={refreshData}
        refreshDisabled={loading}
        compact={true}
        actions={
          canManageEmployees && (
            <Box display="flex" gap={1}>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={handleAddEmployee}
                disabled={loading || !canManageEmployees}
                className="payroll-button payroll-button-primary"
              >
                Add Employee
              </Button>
              <IconButton
                size="small"
                onClick={handleActionMenuOpen}
                sx={{ 
                  bgcolor: 'action.hover',
                  '&:hover': { bgcolor: 'action.selected' }
                }}
              >
                <MoreVertIcon />
              </IconButton>
              <Menu
                anchorEl={actionMenuAnchor}
                open={Boolean(actionMenuAnchor)}
                onClose={handleActionMenuClose}
                transformOrigin={{ horizontal: 'right', vertical: 'top' }}
                anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
              >
                <MenuItem onClick={() => { handleImportEmployees(); handleActionMenuClose(); }}>
                  <ListItemIcon>
                    <UploadFileIcon fontSize="small" />
                  </ListItemIcon>
                  <ListItemText>Import Employees</ListItemText>
                </MenuItem>
              </Menu>
            </Box>
          )
        }
      />

      {/* Error Display */}
      {error && !loading && (
        <div className="payroll-notification payroll-notification-error">
          <Alert 
            severity="error" 
            onClose={() => setError(null)}
            sx={{ mb: 2 }}
            className="payroll-alert"
          >
            {error}
          </Alert>
        </div>
      )}

      {/* Search and Filter Section - Using the reusable FilterBar component */}
      <FilterBar
        searchTerm={searchTerm}
        onSearchChange={setSearchTerm}
        searchPlaceholder="Search by name, employee number, or email"
        filters={filterConfigs}
        onFilterChange={(filterId, value) => {
          if (filterId === 'status') setStatusFilter(value);
          if (filterId === 'jobTitle') setJobTitleFilter(value);
        }}
        compact={true}
      />

      {/* Table Section - Using the reusable DataGrid component */}
      <Box className="payroll-card">
        <DataGrid
          columns={columns as Column<unknown>[]}
          rows={paginatedEmployees}
          totalCount={filteredEmployees.length}
          keyField="id"
          loading={loading && !initialized}
          page={page}
          rowsPerPage={rowsPerPage}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          emptyMessage="No employees found"
          emptyIcon={<PeopleIcon />}
          emptySubMessage={
            (searchTerm || statusFilter !== 'all' || jobTitleFilter !== 'all') ?
            "Try adjusting your search or filters" : undefined
          }
          maxHeight="calc(100vh - 320px)"
        />
      </Box>

      {/* Summary Section - Using the reusable StatsSummary component */}
      <StatsSummary
        stats={[
          {
            label: 'Total Employees',
            value: employees.length
          },
          {
            label: 'Active',
            value: employees.filter(e => e.status === 'active').length,
            color: 'success.main'
          },
          {
            label: 'Inactive',
            value: employees.filter(e => e.status === 'inactive').length,
            color: 'text.secondary'
          }
        ]}
      />

      {/* Confirmation Dialog */}
      <ConfirmDialog
        open={confirmDialogOpen}
        title="Delete Employee"
        content={`Are you sure you want to delete employee ${employeeToDelete?.full_name || employeeToDelete?.employee_no}? This action cannot be undone.`}
        onConfirm={handleConfirmDelete}
        onCancel={handleCancelDelete}
      />

      {/* Employee Dialog */}
      <EmployeeDialog
        open={employeeDialogOpen}
        employee={(() => {
          console.log('=== EmployeeList: Rendering EmployeeDialog ===');
          console.log('employeeDialogOpen:', employeeDialogOpen);
          console.log('selectedEmployee being passed to dialog:', selectedEmployee);
          if (selectedEmployee) {
            console.log('Selected employee details:', {
              id: selectedEmployee.id,
              employee_no: selectedEmployee.employee_no,
              first_name_en: selectedEmployee.first_name_en,
              last_name_en: selectedEmployee.last_name_en,
              first_name_vi: selectedEmployee.first_name_vi,
              last_name_vi: selectedEmployee.last_name_vi,
              full_name: selectedEmployee.full_name
            });
          }
          return selectedEmployee;
        })()}
        onClose={() => {
          console.log('EmployeeDialog onClose called');
          setEmployeeDialogOpen(false);
          setSelectedEmployee(null);
        }}
        onSave={handleEmployeeSave}
      />

      {/* Import Dialog */}
      <EmployeeImportDialog
        open={importDialogOpen}
        onClose={() => setImportDialogOpen(false)}
        onImportComplete={handleImportComplete}
      />

      {/* Notification */}
      {notification.show && (
        <div className={`payroll-notification payroll-notification-${notification.type}`}>
          <div className="payroll-notification-content">
            <span>{notification.message}</span>
            <IconButton 
              size="small" 
              onClick={closeNotification}
              className="payroll-notification-close"
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </div>
        </div>
      )}
    </Box>
  );
};

export default EmployeeList;
